'use client';

import Image from 'next/image';
import React from 'react';

import { classNames } from '@bytel/product-wall/helpers';
import { Link, Text, TextLevels, TextMarkup } from '@bytel/trilogy-react';

import { ProductChildFull } from '@app-types/product';

import { numberToString } from '@helpers/number';

import reparabilityIcon from '@assets/icon-indice-reparation.svg';

import '../assets/styles/reparability.css';

const getNoteColor = (note: number): string => {
    if (note >= 8) return '#009443';
    if (note >= 6) return '#9ecc00';
    if (note >= 4) return '#ffc901';
    if (note >= 2) return '#fe741b';
    return '#d91627';
};

export type ReparabilityProps = {
    reparability: ProductChildFull['reparability'];
};

export function Reparability({ reparability }: Readonly<ReparabilityProps>) {
    const { note, pdf } = reparability!;

    return (
        <>
            <ReparabilityNote value={note} />
            <span className='note-indice'>indice de réparabilité</span>

            <Text level={TextLevels.TWO}>
                <Link href='/guide-pratique/smartphones/indice-de-reparabilite-tout-savoir' blank>
                    L’indice de réparabilité
                </Link>
                <Text markup={TextMarkup.SPAN}>
                    &nbsp;vous informe sur le caractère plus ou moins réparable du produit et sur la possibilité
                    d’allonger sa durée de vie. Plus la note de l’indice est élevée, plus le produit est facile à
                    réparer.
                </Text>
            </Text>
            <Text level={TextLevels.TWO}>
                Les critères et le mode de calcul retenus pour établir cette note sont accessibles depuis le lien
                ci-après.
            </Text>
            {pdf && (
                <Text level={TextLevels.TWO}>
                    <Link href={pdf} blank>
                        Voir le détail de la note
                    </Link>
                </Text>
            )}
        </>
    );
}

export function ReparabilityNote({ value }: Readonly<{ value: number }>) {
    const color = getNoteColor(value);

    return (
        <div
            className='columns is-mobile note is-paddingless is-vcentered is-marginless gap-small'
            style={{ borderColor: color }}
        >
            <div className='column is-narrow is-paddingless is-borderless note-img' style={{ backgroundColor: color }}>
                <Image src={reparabilityIcon} alt='' />
            </div>
            <div
                className='column is-borderless is-paddingless is-vcentered'
                aria-label={`${numberToString(value)} sur 10`}
            >
                <div
                    className={classNames(
                        'real-note has-text-weight-bold has-text-centered',
                        value % 1 === 0 ? 'read-note-integer' : '',
                    )}
                >
                    {numberToString(value)}
                </div>
                <div className='full-note has-text-right'>
                    /<span className='full-note-text'>10</span>
                </div>
            </div>
        </div>
    );
}
